import {
  Box,
  Divider,
  FieldLabel,
  Flex,
  Form,
  MonetaryInputField,
  NumericFormatField,
  SubmitButton,
  Text,
} from '@/components';
import { FundBusinessUpdateFormSchema } from '@/features/project/projects/detail/fund-bussiness/_model/fund-business';
import type { FundBusinessFormValues } from '@/features/project/projects/detail/fund-bussiness/_model/fund-business';
import { useForm } from '@/hooks';
import { zodResolver } from '@hookform/resolvers/zod';
import { isEmpty } from 'lodash-es';
import { type FC, Fragment, useMemo } from 'react';
import { FormProvider, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FundBusinessTitleField } from '../fields/FundBusinessTitleField';

type Props = {
  initialValues?: FundBusinessFormValues;
  onSubmit: (values: FundBusinessFormValues) => void;
  fiscalYear: number;
};

export const SubsidyForm: FC<Props> = ({
  initialValues,
  onSubmit,
  fiscalYear,
}) => {
  const { t } = useTranslation();

  const formMethods = useForm<FundBusinessFormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(FundBusinessUpdateFormSchema),
  });

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isDirty, errors, isSubmitSuccessful },
  } = formMethods;

  const { fields } = useFieldArray({ name: 'performances', control });

  const subsidyFields = useMemo(() => {
    const minYearBoundary = 3;
    const minYear = fiscalYear - minYearBoundary;
    return fields.map((field, fieldIndex) => {
      const year = minYear + fieldIndex;
      const isEstimatedYear = year >= fiscalYear;
      return {
        ...field,
        year,
        decidedAmountInfo: isEstimatedYear
          ? undefined
          : field.decidedAmountInfo,
        yearAmountInfos: field.yearAmountInfos
          .map((value, yearAmountIndex) => ({
            ...value,
            year: minYear + yearAmountIndex,
            originalIndex: yearAmountIndex,
          }))
          .filter((v) => !isEmpty(v.amountInfo)),
      };
    });
  }, [fields, fiscalYear]);

  return (
    <FormProvider {...formMethods}>
      <Form
        leavePageAlertCondition={{
          isDirty,
          isSubmitting,
          isSubmitSuccessful,
        }}
        layout="vertical"
        onFinish={handleSubmit(onSubmit)}
      >
        <FundBusinessTitleField />
        {subsidyFields.map((field, fieldIndex) => {
          const isEstimatedYear = field.year >= fiscalYear;

          return (
            <Fragment key={field.id}>
              {fieldIndex !== 0 && <Divider />}
              <Flex vertical gap={16}>
                <Flex align="center" gap={8}>
                  <Text
                    variant="footnote"
                    color="secondary"
                    text={t('fiscal-year-of-decided-amount-info')}
                  />
                  <Text
                    variant="h5"
                    text={
                      isEstimatedYear
                        ? t('estimate-with-year', { year: field.year })
                        : t('fund-business-with-year', { year: field.year })
                    }
                  />
                </Flex>
                <Form.Item
                  label={<FieldLabel text={t('decided-amount-info')} />}
                >
                  <Flex vertical gap={8}>
                    {field.decidedAmountInfo && (
                      <Flex align="center">
                        <Box flex={2}>
                          <Text text={t('fund-business')} weight="regular" />
                        </Box>
                        <Box flex={2} marginLeft={8} marginRight={16}>
                          <NumericFormatField
                            name={`performances.${fieldIndex}.decidedAmountInfo.count`}
                            control={control}
                            errors={errors}
                            addonAfter={t('number-unit')}
                            marginBottom={0}
                            allowNegative={false}
                          />
                        </Box>
                        <Box flex={5}>
                          <MonetaryInputField
                            name={`performances.${fieldIndex}.decidedAmountInfo.amount`}
                            control={control}
                            errors={errors}
                            unit="thousand"
                            marginBottom={0}
                          />
                        </Box>
                      </Flex>
                    )}
                    {field.estimatedAmountInfo && (
                      <Flex align="center">
                        <Box flex={2}>
                          <Text text={t('estimate')} weight="regular" />
                        </Box>
                        <Box flex={2} marginLeft={8} marginRight={16}>
                          <NumericFormatField
                            name={`performances.${fieldIndex}.estimatedAmountInfo.count`}
                            control={control}
                            errors={errors}
                            addonAfter={t('number-unit')}
                            marginBottom={0}
                            allowNegative={false}
                          />
                        </Box>
                        <Box flex={5}>
                          <MonetaryInputField
                            name={`performances.${fieldIndex}.estimatedAmountInfo.amount`}
                            control={control}
                            unit="thousand"
                            errors={errors}
                            marginBottom={0}
                          />
                        </Box>
                      </Flex>
                    )}
                  </Flex>
                </Form.Item>
              </Flex>
              {field.yearAmountInfos.length > 0 && (
                <Form.Item label={<FieldLabel text={t('expenditure-year')} />}>
                  <Flex vertical gap={8}>
                    {field.yearAmountInfos.map((yearAmountInfo) => {
                      const isEstimatedYear = yearAmountInfo.year >= fiscalYear;
                      const isEstimatedYearOverFiscalYear =
                        yearAmountInfo.year > fiscalYear;

                      return (
                        <Flex key={yearAmountInfo.year} align="center">
                          <Box flex={2}>
                            <Text
                              text={
                                isEstimatedYearOverFiscalYear
                                  ? t(
                                      'estimate-over-current-fiscal-year-with-year',
                                      {
                                        year: yearAmountInfo.year,
                                      },
                                    )
                                  : isEstimatedYear
                                    ? t('estimate-with-year', {
                                        year: yearAmountInfo.year,
                                      })
                                    : yearAmountInfo.year
                              }
                              weight="regular"
                            />
                          </Box>
                          <Box flex={2} marginLeft={8} marginRight={16}>
                            <NumericFormatField
                              name={`performances.${fieldIndex}.yearAmountInfos.${yearAmountInfo.originalIndex}.amountInfo.count`}
                              control={control}
                              errors={errors}
                              addonAfter={t('number-unit')}
                              marginBottom={0}
                              allowNegative={false}
                            />
                          </Box>
                          <Box flex={5}>
                            <MonetaryInputField
                              name={`performances.${fieldIndex}.yearAmountInfos.${yearAmountInfo.originalIndex}.amountInfo.amount`}
                              control={control}
                              unit="thousand"
                              errors={errors}
                              marginBottom={0}
                            />
                          </Box>
                        </Flex>
                      );
                    })}
                  </Flex>
                </Form.Item>
              )}
            </Fragment>
          );
        })}

        <SubmitButton
          text={t('save')}
          disabled={!isDirty}
          loading={isSubmitting}
        />
      </Form>
    </FormProvider>
  );
};
